/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useSyslogForward } from "./hooks";
import { ISyslogForward } from "../../../../store/api/admin/types";
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import { basicDialogSelector, confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import AddEditSyslogReceiver from "./AddEditSyslogReceiver";
import { syslogReceiverDefault } from "../../helpers";
import { constStr } from "../../../../globals/constants";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenToast } from "../../../../store/helpers/helperReducers";
import { initialState } from "../../../../store/api/admin/initialState";
import { setSelectedSyslogReceiver } from "../../../../store/api/admin/reducers";
import { Button } from "../../../ui/button";
import { Icon } from "../../../Shared/Icon";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import { GridColDef } from "@mui/x-data-grid";

const SyslogForwarding: React.FC = () => {
  const {
    permissions,
    data,
    isLoading,
    syslogForwardingColumn,
    addNewSysLogReceiver,
    formData,
    setFormData,
    errMsg,
    setErrMsg,
    submitHandler,
    onBlurHandler,
    modelOpenMode,
    setModelOpenMode,
    handleDeleteSyslogReceiver,
    selectedRow,
    dispatch,
  } = useSyslogForward();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const addNewSysLogReceiverHandler = () => {
    if (data.length >= 3) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "warning",
          message: `A maximum of 3 syslog receiver are allowed. Remove one to configure a new receiver.`,
        })
      );
      return;
    }
    addNewSysLogReceiver();
    setFormData(syslogReceiverDefault);
    setModelOpenMode("Add");
    dispatch(setSelectedSyslogReceiver({ action: "add", ...initialState.selectedSyslogReceiver }));
  };

  const handleEditRow = (row: any) => {
    dispatch(setSelectedSyslogReceiver({ action: "edit", ...row }));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.SYSLOGFORWARDING));
  };

  const handleDeleteRow = (row: any) => {
    dispatch(setSelectedSyslogReceiver({ action: "delete", ...row }));
    dispatch(setConfirmDialog(DIALOGOPENFOR.SYSLOGFORWARDING));
  };

  const actionsColumn: GridColDef = {
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 border-0 bg-transparent rounded-full text-foreground"
            aria-label="Row actions"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setTimeout(() => handleEditRow(params.row), 200)}>
            <Pencil className="h-4 w-4" fill="currentColor" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setTimeout(() => handleDeleteRow(params.row), 200)}>
            <Trash2 className="h-4 w-4" fill="currentColor" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  };

  return (
    <div className="flex flex-col justify-center items-start w-full">
      {openConfirmDialog === DIALOGOPENFOR.SYSLOGFORWARDING && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteSyslogReceiver}>
          Are you sure you want to delete Syslog Receiver <strong>{selectedRow?.hostname || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      {basicDialogOpenFor === DIALOGOPENFOR.SYSLOGFORWARDING && (
        <AddEditSyslogReceiver
          formData={formData}
          setFormData={setFormData}
          errMsg={errMsg}
          setErrMsg={setErrMsg}
          submitHandler={submitHandler}
          modelOpenMode={modelOpenMode}
          onBlurHandler={onBlurHandler}
          handleDeleteSyslogReceiver={handleDeleteSyslogReceiver}
        />
      )}
      <div className="flex items-center justify-end w-full gap-2 text-right px-3 mt-2">
        {permissions?.hasOpCodeGroup("task_management") && (
          <Button variant="outline" onClick={addNewSysLogReceiverHandler} style={{ backgroundColor: '#004f69', color: '#fff', borderColor: '#004f69' }}>
            <Icon name="add" size={18} />
            Add Syslog Collector
          </Button>
        )}
      </div>
      <div className="flex justify-between items-end ml-3 mb-1 mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{`${data?.length || 0} results`}</span>
        </div>
      </div>
      <div className="w-full pl-3 pr-3">
        <CustomDataGrid
          tableId="syslogForwarding"
          rows={data}
          columns={[...syslogForwardingColumn, actionsColumn]}
          rowCount={data?.length}
          loading={isLoading}
          disableColumnMenu={true}
          getRowId={(row: ISyslogForward) => row.id.objectKey}
          hideFooter={true}
          sx={{ minHeight: "430px" }}
          fillContainer
        />
      </div>
    </div>
  );
};
export default SyslogForwarding;
